import type {i18n} from 'i18next';
import {z} from 'zod';

import {isNil} from 'ramda';

import {MoneySchema} from '@dms/api/shared';

import {parseDate} from 'shared/web';

export const CoercedMoneySchema = MoneySchema.extend({
  amount: z.coerce
    .string()
    .nullable()
    .transform((val) => (val === 'undefined' ? null : val)),
  currency: z.string().nullish(),
});

export type CoercedMoney = z.infer<typeof CoercedMoneySchema>;

export const VatDirectionSchema = z.enum(['FROM_PRICE_WITH_VAT', 'FROM_PRICE_WITHOUT_VAT']);
export const CalculationTypeSchema = z.enum(['SIMPLE', 'CALCULATED']);
export const TransactionTypeSchema = z.enum(['ONE_TIME', 'RECURRING']);

export type VatDirection = z.infer<typeof VatDirectionSchema>;
export type CalculationType = z.infer<typeof CalculationTypeSchema>;
export type TransactionType = z.infer<typeof TransactionTypeSchema>;

export const VatCodePriceSchema = z.object({
  withVat: CoercedMoneySchema,
  withoutVat: CoercedMoneySchema,
  vatDirection: VatDirectionSchema.nullable().optional(),
  vatRate: z.string().nullable().optional(),
  vatType: z.string().nullable().optional(),
  isCalculatingVat: z.boolean().optional(),
});
export type VatCodePrice = z.infer<typeof VatCodePriceSchema>;

export const BaseTransactionFormSchema = z.object({
  name: z.string().nonempty(),
  category: z
    .union([z.tuple([z.string()]), z.array(z.unknown()).length(0)])
    .nullable()
    .optional()
    .transform((val) => {
      if (Array.isArray(val) && val.length === 0) {
        return null;
      }
      return val;
    }),
  expectedPrice: VatCodePriceSchema.nullable().transform((val) => {
    if (
      isNil(val?.withVat?.amount) ||
      isNil(val?.withoutVat.amount) ||
      isNil(val?.vatRate) ||
      isNil(val?.vatDirection)
    ) {
      return null;
    }
    return val;
  }),
});

export type BaseTransactionFormSchemaType = z.infer<typeof BaseTransactionFormSchema>;

const BaseFormSchema = z
  .object({
    note: z.string().nonempty().nullable().optional(),
    fileId: z.string().uuid().nullable(),
    transactionType: z.tuple([TransactionTypeSchema]),
    calculationType: z.tuple([CalculationTypeSchema]).nullable(),
    startDate: z.string().nullable(),
    endDate: z.string().nullable(),
    interestPA: z
      .number()
      .nullable()
      .transform((val) => {
        if (isNil(val)) {
          return null;
        }
        return val;
      }),
  })
  .merge(BaseTransactionFormSchema);

export const VehicleTransactionFormSchema = z
  .object({
    realPrice: VatCodePriceSchema.nullable().transform((val) => {
      if (isNil(val?.withVat?.amount) || isNil(val?.withoutVat.amount)) {
        return null;
      }
      return val;
    }),
  })
  .merge(BaseFormSchema);

export type VehicleTransactionFormSchemaType = z.infer<typeof VehicleTransactionFormSchema>;

export const getVehicleTransactionFormSchema = (i18nInstance: i18n) =>
  VehicleTransactionFormSchema.superRefine((data, ctx) => {
    if (
      data.expectedPrice === null &&
      data.realPrice === null &&
      data.transactionType?.[0] === 'ONE_TIME'
    ) {
      ['expectedPrice.withoutVat.amount', 'realPrice.withoutVat.amount'].forEach((path) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18nInstance.t('general.validations.oneOfThoseFieldsIsRequired'),
          path: [path],
        });
      });
    }
  })
    .superRefine((data, ctx) => {
      const transactionType = data.transactionType?.[0];
      const calculationType = data.calculationType?.[0];

      if (transactionType === 'RECURRING') {
        if (!data.startDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18nInstance.t('general.validations.fieldIsRequired'),
            path: ['startDate'],
          });
        }

        if (calculationType === 'CALCULATED' && data.interestPA === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18nInstance.t('general.validations.fieldIsRequired'),
            path: ['interestPA'],
          });
        }

        if (data.realPrice === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18nInstance.t('general.validations.fieldIsRequired'),
            path: ['realPrice.withoutVat.amount'],
          });
        }
      }
    })
    .superRefine((data, ctx) => {
      // Validate that startDate is earlier than endDate
      if (data.startDate && data.endDate) {
        const startDateObj = parseDate(data.startDate);
        const endDateObj = parseDate(data.endDate);

        if (startDateObj >= endDateObj) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18nInstance.t('general.validations.startDateMustBeBeforeEndDate'),
            path: ['startDate'],
          });
        }
      }
    })
    .superRefine((data, ctx) => {
      // Only require vatDirection, vatRate and vatType for realPrice when transaction type is not recurring
      // expectedPrice always needs VAT fields regardless of transaction type
      const transactionType = data.transactionType?.[0];

      // Always validate expectedPrice has VAT fields if it exists
      if (data.expectedPrice) {
        if (!data.expectedPrice.vatDirection) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18nInstance.t('general.validations.fieldIsRequired'),
            path: ['expectedPrice.vatDirection'],
          });
        }

        if (!data.expectedPrice.vatRate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18nInstance.t('general.validations.fieldIsRequired'),
            path: ['expectedPrice.vatRate'],
          });
        }

        if (!data.expectedPrice.vatType) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18nInstance.t('general.validations.fieldIsRequired'),
            path: ['expectedPrice.vatType'],
          });
        }
      }

      // For realPrice, only require VAT fields when transaction is not recurring
      if (data.realPrice && transactionType !== 'RECURRING') {
        if (!data.realPrice.vatDirection) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18nInstance.t('general.validations.fieldIsRequired'),
            path: ['realPrice.vatDirection'],
          });
        }

        if (!data.realPrice.vatRate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18nInstance.t('general.validations.fieldIsRequired'),
            path: ['realPrice.vatRate'],
          });
        }

        if (!data.realPrice.vatType) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18nInstance.t('general.validations.fieldIsRequired'),
            path: ['realPrice.vatType'],
          });
        }
      }
    });
